import { Platform, StyleSheet } from "react-native";

const inter = (weight: "400" | "500" | "600" | "700") =>
  Platform.select({
    ios: {
      fontFamily: weight === "400" ? "Inter-Regular" : weight === "500" ? "Inter-Medium" : weight === "600" ? "Inter-SemiBold" : "Inter-Bold",
      fontWeight: weight as any,
    },
    android: {
      fontFamily: weight === "400" ? "Inter-Regular" : weight === "500" ? "Inter-Medium" : weight === "600" ? "Inter-SemiBold" : "Inter-Bold",
      fontWeight: weight as any,
    },
    default: { fontFamily: "Inter, sans-serif", fontWeight: weight as any },
  }) as { fontFamily: string; fontWeight: any };

export default StyleSheet.create({
  // ── Screen root ──────────────────────────────────────────────────────────
  screenRoot: {
    flex: 1,
    backgroundColor: "#f3f4f8",
  },
  container: {
    flex: 1,
    width: "100%",
    paddingHorizontal: 24,
    paddingVertical: 0,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 48,
  },

  // ── Page header ───────────────────────────────────────────────────────────
  pageHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  pageTitleBlock: {
    flex: 1,
    minWidth: 200,
  },
  breadcrumb: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
  },
  breadcrumbLink: {
    fontSize: 13,
    color: "#2563eb",
    ...inter("500"),
  },
  breadcrumbSep: {
    fontSize: 13,
    color: "#9ca3af",
    marginHorizontal: 2,
  },
  breadcrumbCurrent: {
    fontSize: 13,
    color: "#374151",
    ...inter("500"),
  },
  pageActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },

  // ── Buttons ───────────────────────────────────────────────────────────────
  btnGhost: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
  },
  btnGhostText: {
    fontSize: 13,
    color: "#374151",
    ...inter("500"),
  },
  btnGhostSm: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
  },
  btnGhostSmText: {
    fontSize: 12,
    color: "#374151",
    ...inter("500"),
  },
  btnSecondary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: "#034895",
    backgroundColor: "#ffffff",
    ...Platform.select({
      web: {
        cursor: "pointer",
        transitionProperty: "background, border-color",
        transitionDuration: "120ms",
      } as any,
    }),
  },
  btnSecondaryText: {
    fontSize: 13,
    color: "#034895",
    ...inter("500"),
  },
  btnSecondaryHover: {
    backgroundColor: "#F3F7FD",
  },
  btnPrimary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: "#034895",
    ...Platform.select({
      web: {
        cursor: "pointer",
        transitionProperty: "background",
        transitionDuration: "120ms",
      } as any,
    }),
  },
  btnPrimaryText: {
    fontSize: 12,
    color: "#ffffff",
    ...inter("600"),
  },
  btnPrimaryHover: {
    backgroundColor: "#01346F",
  },
  btnDisabled: {
    opacity: 0.45,
  },
  btnPressed: {
    opacity: 0.75,
  },
  editActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },

  // ── Detail layout (2-col on wide screens, stacked on narrow) ─────────────
  detailLayout: {
    flexDirection: "row",
    gap: 24,
    alignItems: "flex-start",
  },
  detailMain: {
    flex: 1,
    minWidth: 0,
    gap: 16,
  },
  detailSide: {
    width: 340,
    flexShrink: 0,
  },

  // ── Closed Banner (single-line compact alert) ────────────────────────────
  closedBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    height: 48,
    backgroundColor: "#fef3c7",
    borderLeftWidth: 4,
    borderLeftColor: "#f59e0b",
    borderRadius: 8,
    paddingHorizontal: 16,
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
  },
  closedBannerText: {
    fontSize: 13,
    color: "#92400e",
    ...inter("500"),
  },
  closedBannerLink: {
    fontSize: 13,
    color: "#d97706",
    ...inter("600"),
    textDecorationLine: "underline",
  },

  // ── Hero ─────────────────────────────────────────────────────────────────
  detailHero: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 20,
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    gap: 10,
    ...Platform.select({ web: { overflow: "visible" } as any }),
  },
  dhTop: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  dhIdBlock: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  dhId: {
    fontSize: 14,
    color: "#005CBC",
    ...inter("700"),
    marginRight: 4,
  },
  dhTitle: {
    fontSize: 24,
    color: "#2D2D2D",
    ...inter("700"),
    lineHeight: 31,
  },
  reopenedTagWrapWeb: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 2,
  },
  reopenedTagPillWeb: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "#DCFCE7",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  reopenedTagPillTextWeb: {
    color: "#166534",
    fontSize: 11,
    ...inter("700"),
  },
  reopenedTagLinkWeb: {
    color: "#034895",
    fontSize: 12,
    ...inter("700"),
    textDecorationLine: "underline",
  },
  dhMeta: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    marginTop: 4,
  },
  dhMetaItem: {
    fontSize: 12,
    color: "#585858",
    ...inter("400"),
  },
  dhMetaLabel: {
    color: "#3A3A3A",
    ...inter("600"),
  },

  // ── Badges ────────────────────────────────────────────────────────────────
  badgeSm: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  badgeDotSm: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  badgeSmText: {
    fontSize: 12,
    ...inter("700"),
  },
  badgeServiceNow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    backgroundColor: "#CCEAF6",
  },
  badgeServiceNowText: {
    fontSize: 12,
    color: "#0078A7",
    ...inter("600"),
  },

  // ── Detail Section card ───────────────────────────────────────────────────
  detailSection: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    shadowColor: "#005EC0",
    shadowOpacity: 0.08,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 2,
    ...Platform.select({ web: { overflow: "visible" }, default: { overflow: "visible" } }),
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  sectionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  sectionTitle: {
    fontSize: 14,
    color: "#111827",
    ...inter("700"),
  },
  sectionSubtitle: {
    fontSize: 12,
    color: "#9ca3af",
    ...inter("400"),
    marginLeft: 4,
  },
  sectionBody: {
    padding: 20,
    gap: 0,
    ...Platform.select({ web: { overflow: "visible" }, default: {} }),
  },
  countPill: {
    backgroundColor: "#e0f2fe",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginLeft: 6,
  },
  countPillText: {
    fontSize: 11,
    color: "#0369a1",
    ...inter("600"),
  },

  // ── KV grid ───────────────────────────────────────────────────────────────
  kvGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    rowGap: 14,
  },
  kvItem: {
    width: "50%",
    paddingRight: 24,
    gap: 4,
  },
  kvItemFull: {
    width: "100%",
    paddingRight: 0,
    gap: 4,
  },
  kvLabel: {
    fontSize: 11,
    color: "#656565",
    ...inter("600"),
    textTransform: "uppercase",
    letterSpacing: 0.44,
  },
  kvValue: {
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("400"),
    lineHeight: 20,
  },
  kvValueDescription: {
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("400"),
    lineHeight: 21,
    backgroundColor: "#F8F8F8",
    padding: 12,
    borderRadius: 6,
  },
  kvAttachmentList: {
    gap: 4,
  },
  kvAttachmentRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
  },
  kvAttachmentLink: {
    fontSize: 13,
    color: "#034895",
    ...inter("500"),
    textDecorationLine: "underline",
  },
  kvAttachmentSize: {
    fontSize: 12,
    color: "#6B7280",
    ...inter("400"),
  },

  // Merge impact review note shown below People/Loads Impacted fields
  mergeImpactNote: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
    backgroundColor: "#FEF1B9",
    borderWidth: 1,
    borderColor: "#F2DF9A",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 7,
    width: "100%" as any,
  },
  mergeImpactNoteText: {
    flex: 1,
    fontSize: 12,
    color: "#8B6508",
    ...inter("500"),
    lineHeight: 17,
  },

  // ── Merged Tickets Section ──────────────────────────────────────────────────
  mergedTicketRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    paddingVertical: 12,
  },
  mergedTicketRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  mergedTicketIconWrap: {
    width: 26,
    height: 26,
    borderRadius: 6,
    backgroundColor: "#EFEDFD",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  mergedTicketInfo: {
    flex: 1,
    gap: 3,
  },
  mergedTicketIdRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  mergedTicketId: {
    fontSize: 12,
    color: "#034895",
    ...inter("700"),
    fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
  },
  mergedTicketTitle: {
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("500"),
    lineHeight: 19,
  },
  mergedTicketMeta: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 2,
  },
  mergedTicketMetaText: {
    fontSize: 11,
    color: "#6B7280",
    ...inter("400"),
  },
  mergedTicketMetaValue: {
    color: "#374151",
    ...inter("500"),
  },

  // ── Edit form ─────────────────────────────────────────────────────────────
  editForm: {
    gap: 14,
  },
  formRow: {
    flexDirection: "row",
    gap: 14,
    flexWrap: "wrap",
  },
  editFieldWrap: {
    flex: 1,
    minWidth: 180,
    gap: 4,
  },
  editFieldFull: {
    width: "100%",
    flex: 0,
  },
  editFieldLabel: {
    fontSize: 12,
    color: "#374151",
    ...inter("600"),
    marginBottom: 4,
  },
  editRequiredMark: {
    color: "#C12C01",
    ...inter("600"),
  },
  editInput: {
    height: 38,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 7,
    paddingHorizontal: 10,
    fontSize: 13,
    color: "#111827",
    backgroundColor: "#ffffff",
    ...inter("400"),
  },
  editSelectWrap: {
    position: "relative",
    ...Platform.select({ web: { zIndex: 40 } as any, default: {} }),
  },
  editSelectTrigger: {
    height: 38,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 7,
    paddingHorizontal: 10,
    backgroundColor: "#ffffff",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editSelectTriggerDisabled: {
    backgroundColor: "#f8fafc",
    borderColor: "#e5e7eb",
  },
  editSelectText: {
    fontSize: 13,
    color: "#111827",
    ...inter("400"),
  },
  editSelectPlaceholder: {
    color: "#94a3b8",
  },
  editSelectMenu: {
    position: "absolute",
    top: 40,
    left: 0,
    right: 0,
    maxHeight: 220,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
    zIndex: 120,
  },
  editSelectMenuItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  editSelectMenuItemActive: {
    backgroundColor: "#eff6ff",
  },
  editSelectMenuItemText: {
    fontSize: 13,
    color: "#374151",
    ...inter("400"),
  },
  editSelectMenuItemTextActive: {
    color: "#1d4ed8",
    ...inter("600"),
  },
  editTextarea: {
    height: 120,
    paddingTop: 10,
    textAlignVertical: "top",
  },
  helpText: {
    fontSize: 11,
    color: "#9ca3af",
    marginTop: 2,
    ...inter("400"),
  },
  editErrorText: {
    fontSize: 11,
    color: "#c12c01",
    marginTop: 2,
    ...inter("500"),
  },

  // ── Affected Locations ────────────────────────────────────────────────────
  impactSummary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    backgroundColor: "#F3F7FD",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 16,
  },
  impactBigNum: {
    fontSize: 32,
    color: "#034895",
    lineHeight: 32,
    ...inter("700"),
  },
  impactTitle: {
    fontSize: 14,
    color: "#2D2D2D",
    ...inter("700"),
  },
  impactSubtitle: {
    fontSize: 13,
    color: "#4B4B4B",
    ...inter("400"),
    marginTop: 2,
  },
  locationHeaderActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  manageLocationsBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#edf2fa",
    borderRadius: 100,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  manageLocationsBtnText: {
    fontSize: 12,
    color: "#2D2D2D",
    ...inter("600"),
  },
  doneBtn: {
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  doneBtnText: {
    fontSize: 12,
    color: "#2D2D2D",
    ...inter("600"),
  },
  locationFieldWrap: {
    marginBottom: 12,
    zIndex: 20,
  },
  locationFieldWrapRaised: {
    zIndex: 120,
    ...Platform.select({ web: ({ position: "relative" } as any), default: {} }),
  },
  locationFieldWrapLowered: {
    zIndex: 1,
  },
  locationFieldLabel: {
    fontSize: 12,
    color: "#4B4B4B",
    ...inter("600"),
    marginBottom: 6,
  },
  locationAddRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  locationPickerWrap: {
    flex: 1,
    position: "relative" as const,
    zIndex: 40,
  },
  locationPickerTrigger: {
    minHeight: 40,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#ffffff",
  },
  locationPickerText: {
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("500"),
    flex: 1,
  },
  locationPickerPlaceholder: {
    color: "#656565",
  },
  locationPickerMenu: {
    position: "absolute" as const,
    top: 44,
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    zIndex: 999,
    maxHeight: 220,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 6,
    ...Platform.select({ web: ({ overflowY: "auto", overscrollBehavior: "contain" } as any), default: {} }),
  },
  locationPickerMenuHeader: {
    backgroundColor: "#7a7a7a",
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  locationPickerMenuHeaderText: {
    fontSize: 12,
    color: "#ffffff",
    ...inter("500"),
  },
  locationPickerItem: {
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
  },
  locationPickerItemDisabled: {
    backgroundColor: "#F8F8F8",
  },
  locationPickerItemText: {
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("500"),
  },
  locationPickerItemTextDisabled: {
    color: "#818181",
  },
  locationAddBtn: {
    minHeight: 40,
    paddingHorizontal: 16,
    borderRadius: 100,
    backgroundColor: "#034895",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  locationAddBtnDisabled: {
    backgroundColor: "#E6E6E6",
  },
  locationAddBtnText: {
    fontSize: 12,
    color: "#ffffff",
    ...inter("700"),
  },
  locationAddBtnTextDisabled: {
    color: "#818181",
  },
  locationGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  locationCard: {
    width: "49%",
    borderWidth: 1,
    borderColor: "#E6E6E6",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 4,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#ffffff",
  },
  locationCardHovered: {
    borderColor: "#82CDF8",
  },
  locationCardSubmitter: {
    borderColor: "#C6DCFB",
    backgroundColor: "#E8F0FB",
  },
  locationCardName: {
    fontSize: 13,
    color: "#3A3A3A",
    ...inter("500"),
    flex: 1,
  },
  locationCardNameSubmitter: {
    color: "#034895",
    ...inter("600"),
  },
  locationCardSubmitterText: {
    color: "#034895",
    fontSize: 11,
    ...inter("400"),
    fontStyle: "italic",
  },
  locationCardRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  locationCardTime: {
    fontSize: 11,
    color: "#656565",
    ...inter("400"),
  },
  locationRemoveBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
  },
  locationRemoveBtnHover: {
    backgroundColor: "#FFE5DE",
  },
  locationRemoveBtnDisabled: {
    opacity: 0.3,
  },
  locationRow: {
    width: "49%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#F8F8F8",
    borderRadius: 6,
  },
  locationRowSubmitter: {
    backgroundColor: "#E8F0FB",
  },
  locationName: {
    fontSize: 13,
    color: "#3A3A3A",
    ...inter("500"),
    flex: 1,
  },
  locationNameSubmitter: {
    color: "#034895",
    ...inter("600"),
  },
  locationSapId: {
    fontSize: 11,
    color: "#656565",
    ...inter("500"),
  },
  locationSubmitterTag: {
    fontSize: 11,
    color: "#656565",
    ...inter("400"),
    fontStyle: "italic",
  },
  locationTime: {
    fontSize: 11,
    color: "#656565",
    ...inter("400"),
  },
  showMoreBtn: {
    marginTop: 12,
    alignSelf: "flex-start" as const,
  },

  // ── Notes ─────────────────────────────────────────────────────────────────
  noteComposer: {
    borderWidth: 1,
    borderColor: "#F2DF9A",
    borderRadius: 8,
    padding: 14,
    marginBottom: 20,
    backgroundColor: "#FEF1B9",
  },
  notesListScroll: {
    maxHeight: Platform.OS === "web" ? 420 : 360,
  },
  notesListContent: {
    gap: 12,
    paddingBottom: 4,
  },
  noteInput: {
    minHeight: 44,
    fontSize: 13,
    color: "#111827",
    borderWidth: 1,
    borderColor: "#F2DF9A",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: "#ffffff",
    ...inter("400"),
    textAlignVertical: "top",
  },
  noteComposerFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
  },
  noteHint: {
    fontSize: 11,
    color: "#656565",
    ...inter("400"),
  },
  noteItem: {
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#E6E6E6",
    borderLeftWidth: 3,
    borderLeftColor: "#FCC42B",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 6,
  },
  noteHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  noteAuthorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: {
    fontSize: 10,
    color: "#ffffff",
    ...inter("700"),
  },
  noteAuthorName: {
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("700"),
  },
  noteAuthorRole: {
    fontSize: 11,
    color: "#656565",
    ...inter("500"),
  },
  noteTime: {
    fontSize: 11,
    color: "#656565",
    ...inter("400"),
  },
  noteBody: {
    fontSize: 13,
    color: "#3A3A3A",
    lineHeight: 20,
    ...inter("400"),
  },

  // ── Activity Stream ───────────────────────────────────────────────────────
  activityWrap: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    overflow: "hidden",
    flex: 1,
  },
  activityHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  activityTitle: {
    fontSize: 14,
    color: "#111827",
    ...inter("700"),
  },
  activityCount: {
    fontSize: 12,
    color: "#2563eb",
    ...inter("500"),
  },
  activityBody: {
    maxHeight: 380,
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  activityItem: {
    flexDirection: "row",
    gap: 10,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  aiIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  aiIconText: {
    fontSize: 10,
    color: "#ffffff",
    ...inter("700"),
  },
  aiContent: {
    flex: 1,
    gap: 4,
  },
  aiTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  aiAuthorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  aiAuthor: {
    fontSize: 12,
    color: "#111827",
    ...inter("600"),
  },
  aiRole: {
    fontSize: 11,
    color: "#656565",
    ...inter("500"),
  },
  aiTime: {
    fontSize: 11,
    color: "#656565",
    ...inter("400"),
  },
  aiBody: {
    fontSize: 13,
    color: "#3A3A3A",
    lineHeight: 20,
    ...inter("400"),
  },
  aiBodyDefault: {
    backgroundColor: "#F8F8F8",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
  },
  aiBodySystem: {
    backgroundColor: "transparent",
    paddingHorizontal: 0,
    paddingVertical: 0,
    color: "#585858",
    fontSize: 12,
    fontStyle: "italic",
  },
  aiBodyServiceNow: {
    backgroundColor: "#CCEAF6",
    borderLeftWidth: 3,
    borderLeftColor: "#0078A7",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
  },
  aiBodySustain: {
    backgroundColor: "#F3F2FE",
    borderLeftWidth: 3,
    borderLeftColor: "#7871E2",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
  },
  aiSystemRow: {
    flexDirection: "row" as const,
    alignItems: "flex-start" as const,
    gap: 6,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
    flexWrap: "wrap" as const,
  },
  aiSystemIcon: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#e5e7eb",
    alignItems: "center" as const,
    justifyContent: "center" as const,
    flexShrink: 0,
  },
  newActivityBanner: {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 8,
    backgroundColor: "#EBF3FF",
    borderWidth: 1,
    borderColor: "#93C5FD",
    borderRadius: 8,
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    cursor: "pointer" as any,
  },
  newActivityBannerText: {
    flex: 1,
    fontSize: 13,
    color: "#1d4ed8",
    ...inter("600"),
  },

  // ── Comment Composer ──────────────────────────────────────────────────────
  commentComposer: {
    margin: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#fafafa",
  },
  commentInput: {
    minHeight: 64,
    fontSize: 12,
    color: "#111827",
    ...inter("400"),
    textAlignVertical: "top",
  },
  commentFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
  },
  commentHint: {
    fontSize: 11,
    color: "#9ca3af",
    ...inter("400"),
  },
  // ── Commenter badge ────────────────────────────────────────────────────────
  commenterRow: {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 8,
    marginBottom: 8,
  },
  commenterBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center" as const,
    justifyContent: "center" as const,
  },
  commenterInitials: {
    fontSize: 9,
    color: "#ffffff",
    ...inter("700"),
  },
  commenterName: {
    fontSize: 12,
    color: "#374151",
    ...inter("600"),
  },
  // ── Character counter ─────────────────────────────────────────────────────
  charCounter: {
    fontSize: 11,
    color: "#9ca3af",
    textAlign: "right" as const,
    marginTop: 2,
    marginBottom: 6,
    ...inter("400"),
  },
  charCounterWarn: {
    color: "#dc2626",
  },
  // ── Image thumbnails (composer) ───────────────────────────────────────────
  thumbRow: {
    flexDirection: "row" as const,
    marginBottom: 8,
  },
  thumbWrap: {
    width: 72,
    height: 72,
    borderRadius: 8,
    marginRight: 8,
    position: "relative" as const,
    overflow: "hidden" as const,
  },
  thumbImg: {
    width: 72,
    height: 72,
    borderRadius: 8,
  },
  thumbRemove: {
    position: "absolute" as const,
    top: 3,
    right: 3,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: "rgba(0,0,0,0.55)",
    alignItems: "center" as const,
    justifyContent: "center" as const,
  },
  // ── Inline error ──────────────────────────────────────────────────────────
  composerError: {
    fontSize: 12,
    color: "#dc2626",
    marginBottom: 6,
    ...inter("400"),
  },
  // ── Composer footer ───────────────────────────────────────────────────────
  composerFooter: {
    flexDirection: "row" as const,
    justifyContent: "space-between" as const,
    alignItems: "center" as const,
    marginTop: 4,
    gap: 8,
  },
  attachBtn: {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 6,
    backgroundColor: "#ffffff",
  },
  attachBtnText: {
    fontSize: 12,
    color: "#374151",
    ...inter("500"),
  },
  submitBtn: {
    flexShrink: 0,
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 6,
    backgroundColor: "#005CBC",
  },
  submitBtnText: {
    fontSize: 12,
    color: "#ffffff",
    ...inter("600"),
  },
  // ── Inline images inside activity items ───────────────────────────────────
  aiImageRow: {
    marginTop: 6,
  },
  aiImageThumbWrap: {
    marginRight: 8,
  },
  aiImageThumb: {
    width: 120,
    height: 90,
    borderRadius: 6,
  },
  // ── Lightbox ─────────────────────────────────────────────────────────────
  lightboxOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.88)",
    alignItems: "center" as const,
    justifyContent: "center" as const,
  },
  lightboxImg: {
    width: "90%" as any,
    height: "80%" as any,
  },
  lightboxClose: {
    position: "absolute" as const,
    top: 48,
    right: 20,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "rgba(0,0,0,0.5)",
    alignItems: "center" as const,
    justifyContent: "center" as const,
  },

  // ── Hero top actions (sustain-only) ──────────────────────────────────────
  heroTopActions: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 12,
    flexShrink: 0,
    ...Platform.select({ web: { overflow: "visible" } as any }),
  },
  statusFieldWrap: {
    gap: 2,
    minWidth: 140,
    ...Platform.select({ web: { overflow: "visible" } as any }),
  },
  statusFieldLabel: {
    fontSize: 10,
    color: "#656565",
    ...inter("700"),
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  statusSelectWrap: {
    position: "relative" as const,
    zIndex: 20,
    ...Platform.select({ web: { overflow: "visible" } as any }),
  },
  statusSelectTrigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    backgroundColor: "#ffffff",
  },
  statusSelectText: {
    fontSize: 12,
    color: "#2D2D2D",
    ...inter("600"),
  },
  statusSelectMenu: {
    position: "absolute" as const,
    top: 34,
    right: 0,
    minWidth: 180,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
    zIndex: 9999,
    ...Platform.select({
      web: {
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        overflow: "hidden",
      } as any,
    }),
  },
  statusSelectMenuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
    backgroundColor: "#ffffff",
  },
  statusSelectMenuItemActive: {
    backgroundColor: "#F3F7FD",
  },
  statusSelectMenuItemText: {
    fontSize: 13,
    color: "#374151",
    ...inter("500"),
  },
  statusSelectMenuItemTextActive: {
    color: "#034895",
    ...inter("600"),
  },
  widespreadBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#034895",
    backgroundColor: "#ffffff",
  },
  widespreadBtnActive: {
    borderColor: "#CC6E16",
    backgroundColor: "#CC6E16",
  },
  widespreadBtnHover: {
    backgroundColor: "#F3F7FD",
  },
  widespreadBtnActiveHover: {
    borderColor: "#C12C01",
    backgroundColor: "#C12C01",
  },
  widespreadBtnText: {
    fontSize: 12,
    color: "#034895",
    ...inter("500"),
  },
  widespreadBtnTextActive: {
    color: "#ffffff",
    ...inter("600"),
  },
  widespreadBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    backgroundColor: "#FFE9D1",
  },
  widespreadBadgeText: {
    fontSize: 11,
    color: "#CC6E16",
    ...inter("700"),
    letterSpacing: 0.5,
  },
  inactiveTeamText: {
    fontSize: 12,
    color: "#C12C01",
    ...inter("700"),
  },

  // ── Assigned Team reassignment confirmation modal ────────────────────────
  reassignBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  reassignDialog: {
    backgroundColor: "#ffffff",
    borderRadius: 14,
    padding: 24,
    width: "100%",
    maxWidth: 420,
    shadowColor: "#000",
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
    elevation: 24,
  },
  reassignDialogTitle: {
    fontSize: 18,
    color: "#111827",
    ...inter("700"),
    marginBottom: 12,
  },
  reassignDialogBody: {
    fontSize: 14,
    color: "#374151",
    lineHeight: 22,
    marginBottom: 24,
  },
  reassignDialogActions: {
    flexDirection: "row",
    gap: 10,
    justifyContent: "flex-end",
  },
  reassignBtnNo: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
  },
  reassignBtnNoText: {
    fontSize: 14,
    color: "#374151",
    ...inter("600"),
  },
  reassignBtnYes: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: "#034895",
  },
  reassignBtnYesText: {
    fontSize: 14,
    color: "#ffffff",
    ...inter("700"),
  },

  // ── Triage Card ───────────────────────────────────────────────────────────
  triageCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#DDDFFD",
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    overflow: "visible",
    marginBottom: 16,
    ...Platform.select({
      web: {
        backgroundImage: "linear-gradient(180deg, #E1E3FD 0%, #ffffff 60%)",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "0 0",
      } as any,
    }),
  },
  triageCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(120, 113, 226, 0.15)",
  },
  triageCardLabel: {
    fontSize: 11,
    color: "#7871E2",
    ...inter("700"),
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  triageBody: {
    padding: 20,
    gap: 16,
    zIndex: 10,
  },
  triageRow: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
    zIndex: 20,
  },
  triageFieldWrap: {
    flex: 1,
    minWidth: 180,
    gap: 6,
    zIndex: 20,
  },
  triageLabel: {
    fontSize: 11,
    color: "#9ca3af",
    ...inter("600"),
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  triageLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  triageSelectWrap: {
    position: "relative" as const,
    zIndex: 50,
  },
  triageSelectTrigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    backgroundColor: "#ffffff",
  },
  triageSelectText: {
    fontSize: 13,
    color: "#111827",
    ...inter("500"),
    flex: 1,
  },
  uncategorizedText: {
    color: "#f59e0b",
    ...inter("600"),
  },
  triageIncidentInput: {
    fontSize: 13,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    backgroundColor: "#ffffff",
    color: "#111827",
  },
  triageWarningBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: "#fef3c7",
    borderWidth: 1,
    borderColor: "#fcd34d",
    marginTop: 4,
  },
  triageWarningText: {
    fontSize: 12,
    color: "#92400e",
    ...inter("500"),
    flex: 1,
  },
  triageSelectMenu: {
    position: "absolute" as const,
    top: 40,
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
    zIndex: 999,
    maxHeight: 300,
    overflow: "scroll" as any,
    ...Platform.select({
      web: {
        overflowY: "auto" as any,
        overflowX: "hidden" as any,
      },
    }),
  },
  triageSelectMenuItem: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  triageSelectMenuItemActive: {
    backgroundColor: "#F3F7FD",
  },
  triageSelectMenuItemText: {
    fontSize: 13,
    color: "#374151",
    ...inter("500"),
  },
  triageSelectMenuItemTextActive: {
    color: "#034895",
    ...inter("600"),
  },
  triageReadOnlyWrap: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    backgroundColor: "#F8F8F8",
    gap: 2,
  },
  triageReadOnlyText: {
    fontSize: 13,
    color: "#111827",
    ...inter("500"),
  },
  // Triage action variations (used by Access Request simplified flow)
  triagePrimaryBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#034895",
  },
  triagePrimaryBtnText: {
    fontSize: 13,
    color: "#ffffff",
    ...inter("600"),
  },
  triageOutlineBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#1E40AF",
    backgroundColor: "#ffffff",
  },
  triageOutlineBtnText: {
    fontSize: 13,
    color: "#1E40AF",
    ...inter("600"),
  },
  triagePlainBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#ffffff",
  },
  triagePlainBtnText: {
    fontSize: 13,
    color: "#6B7280",
    ...inter("600"),
  },
  triageBtnDisabled: {
    opacity: 0.45,
  },
  modNotesLabel: {
    fontSize: 12,
    color: "#374151",
    ...inter("600"),
    marginBottom: 6,
  },
  modNotesInput: {
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 13,
    backgroundColor: "#ffffff",
    color: "#111827",
    textAlignVertical: "top",
  },
  // Modal aliases used by TriageCard (map to existing detail modal styles)
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.35)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  denyModal: {
    width: "100%",
    maxWidth: 720,
    maxHeight: "90%",
    backgroundColor: "#ffffff",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    elevation: 12,
    overflow: "hidden",
    padding: 16,
  },
  denyTitle: {
    fontSize: 13,
    color: "#374151",
    ...inter("500"),
    marginBottom: 8,
  },
  denyInput: {
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 13,
    backgroundColor: "#ffffff",
    color: "#111827",
    textAlignVertical: "top",
  },
  triageAdoLinkWrap: {
    flexDirection: "column",
    gap: 4,
  },
  adoLinkText: {
    fontSize: 13,
    color: "#034895",
    textDecorationLine: "underline",
    ...inter("600"),
  },
  syncInfoText: {
    fontSize: 11,
    color: "#585858",
    ...inter("400"),
    textAlign: "right",
  },
  syncStatusText: {
    color: "#3A3A3A",
    ...inter("600"),
  },
  triageSyncRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  syncBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: "#CCEAF6",
  },
  syncDot: {
    fontSize: 10,
    color: "#027C06",
    lineHeight: 10,
  },
  syncBadgeText: {
    fontSize: 11,
    color: "#0078A7",
    ...inter("700"),
  },
  triageActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(120, 113, 226, 0.15)",
    zIndex: 1,
  },
  triageActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    zIndex: 1,
  },
  triageActionsLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  triageActionsRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  // ── Ticket Detail Modals ───────────────────────────────────────────────
  detailModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.35)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  detailModalCard: {
    width: "100%",
    maxWidth: 720,
    maxHeight: "90%",
    backgroundColor: "#ffffff",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    elevation: 12,
    overflow: "hidden",
  },
  detailModalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#E6E6E6",
  },
  detailModalTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  detailModalTitle: {
    fontSize: 16,
    color: "#2D2D2D",
    ...inter("700"),
  },
  detailModalCloseBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  detailModalBody: {
    paddingHorizontal: 20,
    paddingVertical: 14,
  },
  detailModalBodyText: {
    fontSize: 13,
    color: "#4B4B4B",
    ...inter("400"),
    lineHeight: 20,
    marginBottom: 12,
  },
  detailModalLabel: {
    fontSize: 12,
    color: "#3A3A3A",
    ...inter("600"),
    marginTop: 8,
    marginBottom: 6,
  },
  detailModalSubLabel: {
    fontSize: 11,
    color: "#656565",
    ...inter("400"),
    marginBottom: 8,
  },
  detailModalOptional: {
    fontSize: 11,
    color: "#656565",
    ...inter("400"),
    marginBottom: 6,
    marginTop: -2,
  },
  detailModalTicketCardPrimary: {
    borderWidth: 1,
    borderColor: "#005CBC",
    backgroundColor: "#F3F7FD",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 10,
  },
  detailModalTicketCard: {
    borderWidth: 1,
    borderColor: "#E6E6E6",
    backgroundColor: "#ffffff",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  detailModalTicketCardSelected: {
    borderColor: "#005CBC",
    backgroundColor: "#E8F0FB",
  },
  detailModalTicketContent: {
    flex: 1,
  },
  detailModalTicketMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 6,
  },
  detailModalChip: {
    backgroundColor: "#E1E3FD",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  detailModalChipText: {
    fontSize: 11,
    color: "#7871E2",
    ...inter("700"),
  },
  detailModalMetaText: {
    fontSize: 11,
    color: "#656565",
    ...inter("500"),
  },
  detailModalTicketId: {
    fontSize: 12,
    color: "#005CBC",
    ...inter("700"),
    marginBottom: 2,
  },
  detailModalTicketTitle: {
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("500"),
    lineHeight: 18,
  },
  detailModalInput: {
    minHeight: 88,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("400"),
    marginBottom: 8,
  },
  detailModalInputSingle: {
    minHeight: 40,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("400"),
    marginBottom: 8,
  },
  detailModalInputLarge: {
    minHeight: 180,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 13,
    color: "#2D2D2D",
    ...inter("400"),
    marginBottom: 8,
  },
  detailModalWarningBox: {
    marginTop: 4,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "#FEF1B9",
    borderWidth: 1,
    borderColor: "#F2DF9A",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  detailModalWarningText: {
    flex: 1,
    fontSize: 12,
    color: "#8B6508",
    ...inter("500"),
    lineHeight: 18,
  },
  detailModalSuccessBox: {
    marginBottom: 12,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "#DDF9DA",
    borderWidth: 1,
    borderColor: "#C6E8B9",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  detailModalSuccessText: {
    flex: 1,
    fontSize: 12,
    color: "#027C06",
    ...inter("500"),
    lineHeight: 18,
  },
  detailModalFormGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  detailModalFormField: {
    width: "49%",
  },
  detailModalFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: "#E6E6E6",
  },

  // ── Access Request Triage Card ─────────────────────────────────────────────
  arTriageHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(120, 113, 226, 0.15)",
  },
  arTriageHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  arTriageCheckboxRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
  },
  arTriageCheckbox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: "#C0C0C0",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },
  arTriageCheckboxChecked: {
    borderColor: "#034895",
    backgroundColor: "#034895",
  },
  arTriageCheckboxLabel: {
    fontSize: 13,
    color: "#585858",
    ...inter("400"),
  },
  arTriageDivider: {
    height: 1,
    backgroundColor: "rgba(120, 113, 226, 0.10)",
    marginHorizontal: 0,
  },
  arTriageActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    padding: 20,
    flexWrap: "wrap",
  },
  arBtnApprove: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    backgroundColor: "#034895",
    borderRadius: 24,
    paddingVertical: 11,
    paddingHorizontal: 20,
  },
  arBtnApproveDisabled: {
    backgroundColor: "#9ca3af",
  },
  arBtnApproveText: {
    fontSize: 14,
    color: "#ffffff",
    ...inter("700"),
  },
  arBtnModify: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    borderRadius: 24,
    borderWidth: 1.5,
    borderColor: "#034895",
    paddingVertical: 10,
    paddingHorizontal: 18,
    backgroundColor: "#ffffff",
  },
  arBtnModifyDisabled: {
    borderColor: "#C0C0C0",
  },
  arBtnModifyText: {
    fontSize: 14,
    color: "#034895",
    ...inter("700"),
  },
  arBtnDeny: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 8,
    backgroundColor: "transparent",
  },
  arBtnDenyText: {
    fontSize: 14,
    color: "#374151",
    ...inter("700"),
  },
  arBtnDisabledText: {
    color: "#9ca3af",
  },
  arBtnResolvedByIT: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: "#034895",
    borderRadius: 8,
    paddingVertical: 14,
    marginHorizontal: 20,
    marginBottom: 4,
    marginTop: 8,
  },
  arBtnResolvedByITText: {
    fontSize: 15,
    color: "#ffffff",
    ...inter("700"),
  },
  arModifyNotesWrap: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    gap: 6,
  },
  arModifyNotesLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  arModifyNotesLabel: {
    fontSize: 11,
    color: "#034895",
    ...inter("700"),
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  arModifyNotesRequired: {
    fontSize: 12,
    color: "#E34119",
    ...inter("700"),
  },
  arModifyNotesInput: {
    borderWidth: 1.5,
    borderColor: "#034895",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: "#111827",
    ...inter("400"),
    minHeight: 96,
    backgroundColor: "#ffffff",
  },
  arModifyNotesHint: {
    fontSize: 12,
    color: "#6b7280",
    ...inter("400"),
  },
  arDenyModalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.55)",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  arDenyModal: {
    backgroundColor: "#ffffff",
    borderRadius: 14,
    padding: 22,
    width: "100%",
    maxWidth: 420,
    gap: 14,
    shadowColor: "#000",
    shadowOpacity: 0.3,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    elevation: 20,
  },
  arDenyModalTitle: {
    fontSize: 14,
    color: "#2D2D2D",
    ...inter("500"),
    lineHeight: 20,
  },
  arDenyInput: {
    borderWidth: 1.5,
    borderColor: "#4A90E2",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: "#2D2D2D",
    ...inter("400"),
    backgroundColor: "#ffffff",
    minHeight: 80,
  },
  arDenyActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
  },
  arDenyCancelBtn: {
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 24,
    backgroundColor: "#DBEAFE", //DBEAFE //1D4ED8
    alignItems: "center",
    justifyContent: "center",
  },
  arDenyCancelText: {
    fontSize: 14,
    color: "#2d2d2d",
    ...inter("700"),
  },
  arDenyOkBtn: {
    paddingVertical: 10,
    paddingHorizontal: 28,
    borderRadius: 24,
    backgroundColor: "#1D4ED8",
    alignItems: "center",
    justifyContent: "center",
  },
  arDenyOkText: {
    fontSize: 14,
    color: "#ffffff",
    ...inter("700"),
  },

  // ── Access Request Detail Section ─────────────────────────────────────────
  arDetailCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E6E6E6",
    marginBottom: 16,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.04,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 1,
  },
  arDetailCardTitle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E6E6E6",
  },
  arDetailCardTitleText: {
    fontSize: 15,
    color: "#111827",
    ...inter("700"),
  },
  arDetailBody: {
    padding: 20,
    gap: 0,
  },
  arKvGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 0,
    marginBottom: 20,
  },
  arKvItem: {
    width: "50%",
    paddingRight: 16,
    marginBottom: 16,
  },
  arKvItemFull: {
    width: "100%",
    paddingRight: 0,
    marginBottom: 16,
  },
  arKvLabel: {
    fontSize: 11,
    color: "#9ca3af",
    ...inter("600"),
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  arKvValue: {
    fontSize: 14,
    color: "#111827",
    ...inter("500"),
  },
  // Before / After comparison
  arCompareRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  },
  arCompareBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#E6E6E6",
    borderRadius: 8,
    padding: 14,
    gap: 6,
  },
  arCompareBoxRequested: {
    borderColor: "#93C5FD",
    backgroundColor: "#EFF6FF",
  },
  arCompareLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
  },
  arCompareLabel: {
    fontSize: 11,
    color: "#9ca3af",
    ...inter("700"),
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  arCompareLabelRequested: {
    color: "#005CBC",
  },
  arRoleSwapBadge: {
    backgroundColor: "#FEF1B9",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  arRoleSwapText: {
    fontSize: 10,
    color: "#8B6A12",
    ...inter("700"),
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  arCompareRoleName: {
    fontSize: 15,
    color: "#111827",
    ...inter("700"),
    marginBottom: 2,
  },
  arCompareScopeTier: {
    fontSize: 12,
    color: "#005CBC",
    ...inter("500"),
    marginBottom: 8,
  },
  arCompareArrow: {
    alignItems: "center",
    justifyContent: "center",
    width: 28,
  },
  arChipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  arChip: {
    backgroundColor: "#F3F4F6",
    borderRadius: 16,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  arChipText: {
    fontSize: 12,
    color: "#374151",
    ...inter("500"),
  },

  // Requested / current chip variants
  arChipRequested: {
    backgroundColor: "#ffffff",
    borderColor: "#93C5FD",
    borderWidth: 1,
  },
  arChipTextRequested: {
    color: "#005CBC",
  },
  arChipCurrent: {
    backgroundColor: "#E6F3FF",
    borderColor: "#CCEAF6",
  },
  arChipTextCurrent: {
    color: "#005CBC",
  },
  // Sites to grant table
  arSitesHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  arSitesTitle: {
    fontSize: 15,
    color: "#111827",
    ...inter("700"),
  },
  arSitesSubtitle: {
    fontSize: 13,
    color: "#374151",
    ...inter("600"),
    marginBottom: 10,
  },
  arSitesCount: {
    fontSize: 13,
    color: "#374151",
    ...inter("500"),
  },
  arSitesTable: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 8,
  },
  arSitesTableHead: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  arSitesTableHeadText: {
    fontSize: 11,
    color: "#6b7280",
    ...inter("700"),
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  arSitesTableColSite: {
    flex: 1,
  },
  arSitesTableColCoverage: {
    width: 220,
  },
  arSitesTableColStatus: {
    width: 90,
    alignItems: "flex-end",
  },
  arSitesRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  arSitesRowLast: {
    borderBottomWidth: 0,
  },
  arSitesRowExpanded: {
    backgroundColor: "#FAFAFA",
  },
  arSitesRowRegion: {
    backgroundColor: "#ffffff",
  },
  arSitesRowMarket: {
    backgroundColor: "#FAFAFE",
    paddingLeft: 28,
  },
  arSitesRowSite: {
    paddingLeft: 44,
  },
  arSitesExpandBtn: {
    width: 18,
    height: 18,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 6,
  },
  arSitesCheckbox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: "#C0C0C0",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    flexShrink: 0,
  },
  arSitesCheckboxChecked: {
    borderColor: "#034895",
    backgroundColor: "#034895",
  },
  arSitesRowName: {
    fontSize: 14,
    color: "#005CBC",
    ...inter("600"),
    flex: 1,
  },
  arSitesRowNameSite: {
    fontSize: 13,
    color: "#374151",
    ...inter("400"),
    flex: 1,
  },
  arSitesRowCount: {
    fontSize: 12,
    color: "#9ca3af",
    ...inter("400"),
    marginLeft: 4,
  },
  arSitesCoverage: {
    width: 220,
    fontSize: 12,
    color: "#6b7280",
    ...inter("400"),
  },
  arSitesStatus: {
    width: 90,
    alignItems: "flex-end",
  },
  arSitesStatusGrant: {
    backgroundColor: "#D1FAE5",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignItems: "center",
  },
  arSitesStatusGrantText: {
    fontSize: 12,
    color: "#065F46",
    ...inter("700"),
  },
  arSitesStatusIncluded: {
    backgroundColor: "#DBEAFE",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignItems: "center",
  },
  arSitesStatusIncludedText: {
    fontSize: 12,
    color: "#1D4ED8",
    ...inter("700"),
  },
  arSitesStatusNone: {
    fontSize: 12,
    color: "#9ca3af",
    ...inter("400"),
  },
  arSitesPartialStatus: {
    backgroundColor: "#FEF3C7",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignItems: "center",
  },
  arSitesPartialStatusText: {
    fontSize: 12,
    color: "#92400E",
    ...inter("700"),
  },
  arSitesHint: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
    marginTop: 4,
  },
  arSitesHintText: {
    fontSize: 12,
    color: "#6b7280",
    ...inter("400"),
    flex: 1,
    lineHeight: 18,
  },
  arInfoBox: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "#FEF9C3",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FDE68A",
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 16,
  },
  arInfoBoxText: {
    flex: 1,
    fontSize: 13,
    color: "#713F12",
    ...inter("500"),
    lineHeight: 19,
  },
  // Skeleton shimmer for loading state
  arSkeletonWrap: {
    padding: 20,
    gap: 12,
  },
  arSkeletonRow: {
    flexDirection: "row",
    gap: 12,
  },
  arSkeletonBlock: {
    borderRadius: 6,
    backgroundColor: "#E5E7EB",
  },
  // ── Edit form styles (shown when Modify is active) ──
  arEditFormWrap: {
    marginTop: 4,
    marginBottom: 8,
  },
  arEditFormDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginBottom: 16,
  },
  arEditFormTitle: {
    fontSize: 11,
    ...inter("600"),
    color: "#374151",
    letterSpacing: 0.8,
    marginBottom: 14,
    textTransform: "uppercase",
  },
  arEditFormGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 14,
  },
  arEditFieldWrap: {
    minWidth: 160,
    flex: 1,
    flexBasis: "45%",
  },
  arEditFieldWrapRaised: {
    //zIndex:0,
    ...Platform.select({ web: ({ position: "relative" } as any), default: {} }),
  },
  arEditFieldLabel: {
    fontSize: 10,
    ...inter("600"),
    color: "#6b7280",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  arEditSelectWrap: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 10,
    paddingVertical: Platform.OS === "ios" ? 10 : 8,
  },
  arEditSelectInput: {
    flex: 1,
    fontSize: 13,
    ...inter("500"),
    color: "#111827",
  },
  arEditSelectIcon: {
    marginLeft: 4,
  },
});
